import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API, LOGO_FULL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { KeyRound, CheckCircle2, XCircle, LogIn } from "lucide-react";
import axios from "axios";

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [pwErr, setPwErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const base = `${API}/reset-password/${token}`;

  useEffect(() => {
    axios.get(base).then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "This reset link is invalid or has expired."));
  }, [token]);

  const submit = async () => {
    setPwErr("");
    if (pw.length < 8) { setPwErr("Password must be at least 8 characters."); return; }
    if (pw !== pw2) { setPwErr("Passwords do not match."); return; }
    setBusy(true);
    try { await axios.post(base, { password: pw }); setDone(true); }
    catch (e) { setPwErr(e.response?.data?.detail || "Could not reset your password."); }
    finally { setBusy(false); }
  };

  if (error) return (
    <div className="min-h-screen bg-navy flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl p-8 max-w-md text-center shadow-2xl">
        <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
        <p className="text-navy font-heading font-semibold text-lg">Reset link unavailable</p>
        <p className="text-slate-500 text-sm mt-2">{error}</p>
      </div>
    </div>
  );
  if (!data) return <div className="min-h-screen bg-navy flex items-center justify-center text-white">Loading…</div>;

  return (
    <div className="min-h-screen bg-slate-100">
      <header className="bg-navy px-5 py-5 flex flex-col items-center">
        <img src={LOGO_FULL} alt="True Seal Caulking" className="h-16 object-contain" />
      </header>
      <div className="max-w-md mx-auto p-4 sm:p-6 pb-16">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6 mt-4">
          {done ? (
            <div className="text-center py-4">
              <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-3" />
              <h2 className="font-heading font-bold text-navy text-lg">Password reset</h2>
              <p className="text-slate-500 text-sm mt-2">You can now sign in with your email and new password.</p>
              <Button data-testid="reset-goto-login" onClick={() => navigate("/login")} className="mt-5 bg-navy hover:bg-navy-light py-6 px-8 text-base"><LogIn className="w-5 h-5 mr-2" />Go to Login</Button>
            </div>
          ) : (
            <>
              <h1 className="font-heading font-bold text-navy text-xl flex items-center gap-2"><KeyRound className="w-5 h-5" />Set a new password</h1>
              <p className="text-slate-500 text-sm mt-1 mb-4">For <span className="font-medium text-navy">{data.email}</span></p>
              <label className="text-xs uppercase tracking-wider text-slate-400">New Password</label>
              <Input data-testid="reset-password-input" type="password" value={pw} onChange={(e) => setPw(e.target.value)} className="mt-1 mb-4" placeholder="At least 8 characters" />
              <label className="text-xs uppercase tracking-wider text-slate-400">Confirm Password</label>
              <Input data-testid="reset-password-confirm" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} className="mt-1 mb-2" placeholder="Re-enter password" />
              {pwErr && <p className="text-sm text-red-600 mb-2" data-testid="reset-password-error">{pwErr}</p>}
              <Button data-testid="reset-submit-button" onClick={submit} disabled={busy || !pw || !pw2} className="w-full mt-3 bg-navy hover:bg-navy-light py-6 text-base"><KeyRound className="w-5 h-5 mr-2" />{busy ? "Saving…" : "Reset Password"}</Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
